import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';

// Input validation schemas
const usernameSchema = z.string()
  .min(3, 'نام کاربری باید حداقل ۳ حرف باشد')
  .max(50, 'نام کاربری خیلی طولانی است')
  .regex(/^[a-zA-Z0-9_]+$/, 'نام کاربری فقط باید شامل حروف انگلیسی و اعداد باشد');

const passwordSchema = z.string()
  .min(6, 'رمز عبور باید حداقل ۶ کاراکتر باشد')
  .max(100, 'رمز عبور خیلی طولانی است');

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, username: string, phone: string) => Promise<{ error: Error | null }>;
  signIn: (username: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Generic error messages to hide database details
const GENERIC_ERROR = 'خطایی رخ داد. لطفا دوباره تلاش کنید.';
const INVALID_CREDENTIALS = 'نام کاربری یا رمز عبور اشتباه است';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, username: string, phone: string) => {
    try {
      // Validate inputs to prevent injection
      const usernameResult = usernameSchema.safeParse(username);
      if (!usernameResult.success) {
        throw new Error(usernameResult.error.errors[0].message);
      }
      
      const passwordResult = passwordSchema.safeParse(password);
      if (!passwordResult.success) {
        throw new Error(passwordResult.error.errors[0].message);
      }

      // Sanitize username
      const sanitizedUsername = username.trim().toLowerCase();

      // Check if username already exists using secure RPC
      const { data: existingUser } = await supabase
        .rpc('get_user_email_by_username', { p_username: sanitizedUsername });
      
      if (existingUser) {
        throw new Error('این نام کاربری قبلاً انتخاب شده است');
      }

      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            username: sanitizedUsername,
            phone: phone ? phone.replace(/[^0-9+]/g, '') : '',
          }
        }
      });
      
      if (error) {
        if (error.message.includes('already registered')) {
          throw new Error('این ایمیل قبلاً ثبت شده است');
        }
        throw new Error(GENERIC_ERROR);
      }
      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const signIn = async (username: string, password: string) => {
    try {
      // Sanitize username
      const sanitizedUsername = username.trim().toLowerCase();

      // 1. Get email from username using secure RPC
      const { data: emailData, error: lookupError } = await supabase
        .rpc('get_user_email_by_username', { p_username: sanitizedUsername });

      if (lookupError || !emailData) {
        // Return generic error to prevent username enumeration
        throw new Error(INVALID_CREDENTIALS);
      }

      const email = emailData as string;

      // 2. Sign in with email
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw new Error(INVALID_CREDENTIALS);
      }

      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
